import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TextField, Button, Alert, TablePagination, InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Accordion, AccordionSummary, AccordionDetails, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import API_CONFIG, { getApiUrl } from '../../config/api';

const ADMIN_PASSWORD = 'Admin@123!!!';

const Referrals = () => {
  const [users, setUsers] = useState([]);
  const [order, setOrder] = useState('desc');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(() => {
    // Check if admin is already authenticated via AdminAuthGuard
    return localStorage.getItem('adminAuthenticated') === 'true';
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSurveys, setUserSurveys] = useState([]);
  const [surveyQuestions, setSurveyQuestions] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingUserData, setLoadingUserData] = useState(false);
  const [showAdminPassword, setShowAdminPassword] = useState(false);

  const fetchUsers = async (pw = null) => {
    setLoading(true);
    setError('');
    try {
      // Use provided password, or get from state, or use default admin password
      const adminPassword = pw || password || ADMIN_PASSWORD;

      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.USER.LIST_USERS_WITH_ONBOARDING), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_password: adminPassword,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          order
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setUsers(data.users || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = () => setOrder(order === 'asc' ? 'desc' : 'asc');

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      localStorage.setItem('adminAuthenticated', 'true');
      fetchUsers(password);
    } else {
      setError('Incorrect admin password');
    }
  };

  const fetchUserSurveys = async (userId) => {
    try {
      console.log('Fetching surveys for user:', userId);
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.SURVEYS.LIST) + `?user_id=${userId}`);
      const data = await res.json();
      console.log('Survey API response:', data);
      if (data.status === 'success') {
        return data.surveys || [];
      }
      throw new Error(data.message || 'Failed to fetch surveys');
    } catch (e) {
      console.error('Error fetching user surveys:', e);
      return [];
    }
  };

  const fetchSurveyQuestions = async (surveyId) => {
    try {
      console.log('Fetching questions for survey:', surveyId);
      const res = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.QUESTIONS.LIST) + `?survey_id=${surveyId}`);
      const data = await res.json();
      console.log('Questions API response:', data);
      if (data.status === 'success') {
        return data.questions || [];
      }
      throw new Error(data.message || 'Failed to fetch questions');
    } catch (e) {
      console.error('Error fetching survey questions:', e);
      return [];
    }
  };

  const handleLoginAsUser = (user) => {
    // Store admin impersonation data
    localStorage.setItem('adminImpersonation', JSON.stringify({
      user_id: user.id,
      email: user.email,
      display_name: user.display_name,
      isAdminImpersonation: true,
      timestamp: Date.now()
    }));

    // Navigate to the correct app URL with basename
    const baseUrl = window.location.origin;
    window.open(`${baseUrl}/app/dashboard`, '_blank');
  };

  const handleViewUserAccount = async (user) => {
    setSelectedUser(user);
    setLoadingUserData(true);
    setDialogOpen(true);

    try {
      console.log('Selected user:', user);
      // Fetch user's surveys - using 'id' field which is the user_id
      const surveys = await fetchUserSurveys(user.id);
      setUserSurveys(surveys);

      // Fetch questions for each survey
      const questionsData = {};
      for (const survey of surveys) {
        const questions = await fetchSurveyQuestions(survey.id);
        questionsData[survey.id] = questions;
      }
      setSurveyQuestions(questionsData);
    } catch (e) {
      setError('Failed to load user data: ' + e.message);
    } finally {
      setLoadingUserData(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
    setUserSurveys([]);
    setSurveyQuestions({});
  };

  const handleLogout = () => {
    setAuthenticated(false);
    localStorage.removeItem('adminAuthenticated');
    setPassword('');
    setUsers([]);
    setError('');
  };

  useEffect(() => {
    if (authenticated) {
      // Use the admin password directly when already authenticated
      fetchUsers(ADMIN_PASSWORD);
    }
    // eslint-disable-next-line
  }, [order]);

  // Listen for admin authentication changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'adminAuthenticated') {
        if (e.newValue === 'true') {
          setAuthenticated(true);
        } else {
          setAuthenticated(false);
          setUsers([]);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Global search filter
  const filteredUsers = users.filter(user => {
    if (!search) return true;
    const searchStr = search.toLowerCase();
    return Object.values(user).some(val =>
      (val ? String(val).toLowerCase() : '').includes(searchStr)
    );
  });

  if (!authenticated) {
    return (
      <Box sx={{ maxWidth: 400, mx: 'auto', mt: 8 }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>Admin Login</Typography>
          <TextField
            label="Admin Password"
            type={showAdminPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle admin password visibility"
                    onClick={() => setShowAdminPassword(!showAdminPassword)}
                    onMouseDown={(event) => event.preventDefault()}
                    edge="end"
                  >
                    {showAdminPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button variant="contained" fullWidth onClick={handleLogin}>Login</Button>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </Paper>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" gutterBottom>Referrals & New Users</Typography>
        <Button
          variant="outlined"
          color="error"
          size="small"
          onClick={handleLogout}
        >
          Logout
        </Button>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="From Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
        />
        <TextField
          label="To Date"
          type="date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
        />
        <TextField
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ minWidth: 220 }}
        />
        <Button variant="outlined" onClick={() => fetchUsers(ADMIN_PASSWORD)}>Filter</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>
                <TableSortLabel active direction={order} onClick={handleSort}>
                  Registered At
                </TableSortLabel>
              </TableCell>
              <TableCell>Referral Link</TableCell>
              <TableCell>Referral Code</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Goal</TableCell>
              <TableCell>Feature</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(user => (
              <TableRow key={user.id}>
                <TableCell>{user.email}</TableCell>
                <TableCell>{user.display_name}</TableCell>
                <TableCell>{user.created_at}</TableCell>
                <TableCell style={{ maxWidth: 200, wordBreak: 'break-all' }}>{user.referrer_url || '-'}</TableCell>
                <TableCell>{user.referral_code || '-'}</TableCell>
                <TableCell>{user.source || '-'}</TableCell>
                <TableCell>{user.role || '-'}</TableCell>
                <TableCell>{user.goal || '-'}</TableCell>
                <TableCell>{user.feature || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => handleViewUserAccount(user)}
                    >
                      View Account
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      color="secondary"
                      onClick={() => handleLoginAsUser(user)}
                    >
                      Login as User
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* User Account Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle>
          User Account Details - {selectedUser?.display_name || selectedUser?.email}
        </DialogTitle>
        <DialogContent>
          {loadingUserData ? (
            <Typography>Loading user data...</Typography>
          ) : (
            <Box>
              {/* User Info */}
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>User Information</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                  <Typography><strong>Email:</strong> {selectedUser?.email}</Typography>
                  <Typography><strong>Name:</strong> {selectedUser?.display_name}</Typography>
                  <Typography><strong>User ID:</strong> {selectedUser?.id}</Typography>
                  <Typography><strong>Registered:</strong> {selectedUser?.created_at}</Typography>
                  <Typography><strong>Role:</strong> {selectedUser?.role || 'N/A'}</Typography>
                  <Typography><strong>Goal:</strong> {selectedUser?.goal || 'N/A'}</Typography>
                </Box>
              </Paper>

              {/* Surveys */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Surveys ({userSurveys.length})
                </Typography>
                {userSurveys.length === 0 ? (
                  <Typography color="text.secondary">No surveys created yet</Typography>
                ) : (
                  userSurveys.map((survey, index) => (
                    <Accordion key={survey.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                            {survey.name}
                          </Typography>
                          <Chip
                            label={survey.status}
                            color={survey.status === 'active' ? 'success' : 'default'}
                            size="small"
                          />
                          <Typography variant="caption" color="text.secondary">
                            Created: {new Date(survey.created_at).toLocaleDateString()}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box>
                          <Typography variant="subtitle2" gutterBottom>
                            Questions ({surveyQuestions[survey.id]?.length || 0}):
                          </Typography>
                          {surveyQuestions[survey.id]?.length === 0 ? (
                            <Typography color="text.secondary" sx={{ ml: 2 }}>
                              No questions added yet
                            </Typography>
                          ) : (
                            <Box sx={{ ml: 2 }}>
                              {surveyQuestions[survey.id]?.map((question, qIndex) => (
                                <Box key={question.id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="body2">
                                    <strong>Q{qIndex + 1}:</strong> {question.question_text}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Type: {question.question_type} | Required: {question.is_required ? 'Yes' : 'No'}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                            <Chip label={`ID: ${survey.id}`} size="small" variant="outlined" />
                            <Chip label={`Updated: ${new Date(survey.updated_at).toLocaleDateString()}`} size="small" variant="outlined" />
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Referrals;
